/**
 * The spatial mapping M: true space (heliocentric ecliptic J2000, km, float64) → display space (du = 10⁶ km).
 * Concept §3.2, sprint plan §5.4 and Appendix B.
 *
 * - Global: M_g(p) = g(|p|) · p̂ with g(r) = AU_du · (r / AU)^α. Directions from the Sun stay exact.
 * - Bubble of body B (true radius R, bubble radius R_bub, display radius R'), q = p − b, d = |q|, a = d − R:
 *   M_l(p) = M_g(b) + (R' + h(a)) · q̂ with h(0) = 0 and h strictly increasing, so nothing is ever drawn inside B
 *   and the direction from B is exact.
 *   h(a) = (1 − λ) · H · ln(1 + a / R) + λ · a / 10⁶ km   (λ = localLinear; Realistic λ = 1 ⇒ M_l ≡ M_g)
 * - Transition shell: w = smoothstep((d − (1 − σ)·R_bub) / (σ·R_bub)), M = (1 − w)·M_l + w·M_g.
 *
 * Nested bubbles (Sprint 3, S3-07 / D8): a moon inside its planet's bubble can have its own bubble. Inside it,
 * positions are taken relative to the moon and mapped with the moon's own altitude mapping, on top of the planet's
 * local mapping of the moon's position:
 *   M_m(p) = M_P(moon) + (R′_moon + h_moon(a)) · q̂     (q = p − moon)
 * Its transition shell blends into the planet's *local* mapping (not the global one). The moon's bubble is at least
 * large enough that the planet's local mapping — which compresses radially by H_P / d — keeps the shell clear of the
 * enlarged moon, measured in the planet's local scale. `moonEnlarge` scales the enlargement of moons separately, so
 * the moon bubbles do not overlap (experiment E2).
 */
import type { ScaleProfile } from './scaleProfiles';
import { OVERVIEW } from './scaleProfiles';
import { AU_DU, AU_KM, KM_PER_DU } from './units';
import { type Vec3, vec3 } from './vec3';

export interface MappedBody {
  id: string;
  /** True (equatorial) radius, km. */
  radius: number;
  /** Bubble radius, km; 0 = no bubble (the Sun; moons without a nested bubble). */
  bubbleRadius: number;
  /** Moons: the planet they orbit (their bubble is nested inside the planet's). */
  parent?: string | undefined;
}

/** True body position (km, heliocentric) for the current time, or undefined while the body is not shown. */
export type BodyPositionLookup = (id: string) => Vec3 | undefined;

/** The bubbles containing a point: its planet's and, nested inside, a moon's. */
export interface BubbleChain {
  planet: MappedBody | undefined;
  moon: MappedBody | undefined;
}

/** Radius of a Jupiter-sized body, the reference for enlarged sizes. */
const REF_RADIUS_KM = 71_492;
/**
 * The inner edge of the shell must map at least this many display radii away from the body, so that the global
 * mapping (which compresses radially by α) can never put a point inside the enlarged body.
 */
const MIN_SHELL_CLEARANCE = 1.5;

const NO_MOONS: readonly MappedBody[] = [];

const smoothstep = (x: number): number => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));

/**
 * Moon enlargement per planet, relative to the profile's `moonEnlarge` (experiment E3, S4-06). Earth is enlarged ~70×
 * in Overview and its local mapping squeezes the Moon's 384,400 km, so the Moon needs a large nested bubble to keep its
 * shell clear of the enlarged Moon: at 0.75 that bubble (437,000 km) would swallow Earth; at 0.8 · 0.75 = 0.6 it is
 * 165,000 km, inside half the perigee distance (178,000 km). The Moon is then 7 % of Earth's display radius (truly
 * 27 %) and an orbit 100 km above it stays clearly outside it (0.72 R′). Realistic is unaffected (no enlargement).
 */
export const MOON_ENLARGE_BY_PLANET: Readonly<Record<string, number>> = { earth: 0.8 };

export class SpatialMapping {
  readonly bodies: readonly MappedBody[];
  /** Bodies with a top-level bubble (planets, Pluto, Arrokoth). */
  readonly bubbleBodies: readonly MappedBody[];
  /** Moons with a nested bubble, by planet id. */
  private readonly moonsByParent = new Map<string, MappedBody[]>();
  private readonly byId: Map<string, MappedBody>;
  profile: ScaleProfile;
  private readonly tmpG = vec3();
  private readonly tmpB = vec3();
  private readonly tmpM = vec3();
  private readonly tmpQ2 = vec3();
  private readonly tmpQm = vec3();
  private readonly chain: BubbleChain = { planet: undefined, moon: undefined };

  constructor(bodies: readonly MappedBody[], profile: ScaleProfile = { ...OVERVIEW }) {
    this.bodies = bodies;
    this.bubbleBodies = bodies.filter((b) => b.bubbleRadius > 0 && !b.parent);
    this.byId = new Map(bodies.map((b) => [b.id, b]));
    for (const b of bodies) {
      if (!b.parent || b.bubbleRadius <= 0) continue;
      const list = this.moonsByParent.get(b.parent) ?? [];
      list.push(b);
      this.moonsByParent.set(b.parent, list);
    }
    this.profile = profile;
  }

  /** Moons with a nested bubble inside `planet`'s bubble. */
  moonsOf(planet: string): readonly MappedBody[] {
    return this.moonsByParent.get(planet) ?? NO_MOONS;
  }

  body(id: string): MappedBody | undefined {
    return this.byId.get(id);
  }

  /** g(r) in du for a heliocentric distance r in km. */
  g(r: number): number {
    return r <= 0 ? 0 : AU_DU * Math.pow(r / AU_KM, this.profile.alpha);
  }

  /** Tangential scale of the global mapping at distance r: du per km. */
  tangentialScale(r: number): number {
    return r <= 0 ? 1 / KM_PER_DU : this.g(r) / r;
  }

  /**
   * Display radius R' of a body in du (log-space blend between true and enlarged size). Moons blend with
   * `bodyEnlarge · moonEnlarge` (E2), times a factor for their planet (E3), so they can be enlarged less than planets.
   */
  displayRadius(body: MappedBody): number {
    const p = this.profile;
    const trueDu = body.radius / KM_PER_DU;
    const k = body.parent ? p.bodyEnlarge * p.moonEnlarge * (MOON_ENLARGE_BY_PLANET[body.parent] ?? 1) : p.bodyEnlarge;
    if (k === 0) return trueDu;
    const enlarged = p.enlargedRefDu * Math.pow(body.radius / REF_RADIUS_KM, p.enlargedExponent);
    return Math.exp((1 - k) * Math.log(trueDu) + k * Math.log(enlarged));
  }

  /** Effective bubble radius in km for a body at heliocentric distance `r` (km). */
  bubbleRadius(body: MappedBody, r: number): number {
    const p = this.profile;
    const minimum =
      (MIN_SHELL_CLEARANCE * this.displayRadius(body)) / ((1 - p.sigma) * p.alpha * this.tangentialScale(r));
    return Math.max(body.bubbleRadius * p.bubbleScale, minimum, 2 * body.radius);
  }

  /**
   * Logarithmic altitude scale H in du, chosen so the local mapping lands near the global scale at the bubble edge.
   */
  altitudeScale(body: MappedBody, r: number): number {
    const p = this.profile;
    const rBub = this.bubbleRadius(body, r);
    const match = this.tangentialScale(r) * (p.alpha + (1 - p.alpha) * p.bubbleMatch);
    const rPrime = this.displayRadius(body);
    const h = (rBub * match - rPrime) / Math.log(1 + (rBub - body.radius) / body.radius);
    return Math.max(h, 0.01 * rPrime) * p.hScale;
  }

  /** Altitude mapping h(a) in du for an altitude a in km. */
  altitude(body: MappedBody, r: number, a: number): number {
    const p = this.profile;
    const lin = a / KM_PER_DU;
    if (p.localLinear >= 1) return lin;
    const log = this.altitudeScale(body, r) * Math.log1p(a / body.radius);
    return (1 - p.localLinear) * log + p.localLinear * lin;
  }

  /**
   * Scales of the planet's local mapping at distance `d` (km) from its centre, du per km: radially (dh/da) and
   * tangentially ((R′ + h) / d). Inside a planet bubble a moon's surroundings are squashed radially and stretched
   * tangentially; the moon bubble is sized against the radial scale, its altitude mapping matches a blend of both.
   */
  localScales(planet: MappedBody, r: number, d: number): { rad: number; tan: number } {
    const p = this.profile;
    const a = Math.max(d - planet.radius, 0);
    const rad = (1 - p.localLinear) * (this.altitudeScale(planet, r) / (planet.radius + a)) + p.localLinear / KM_PER_DU;
    const tan = d > 0 ? (this.displayRadius(planet) + this.altitude(planet, r, a)) / d : rad;
    return { rad, tan };
  }

  /**
   * Effective radius (km) of a moon's nested bubble, for the planet at heliocentric distance `r` and the moon at
   * distance `d` from the planet: the catalogue bubble, at least large enough that the planet's local mapping keeps
   * the inner edge of the moon's transition shell 1.5 R′ clear of the enlarged moon, and at least twice the moon.
   */
  moonBubbleRadius(moon: MappedBody, planet: MappedBody, r: number, d: number): number {
    const memo = this.memoFor(moon, r, d);
    if (!Number.isNaN(memo.bubble)) return memo.bubble;
    const p = this.profile;
    const need = MIN_SHELL_CLEARANCE * this.displayRadius(moon);
    // the radial scale shrinks outwards: evaluate it at the far side of a first estimate
    const first = need / ((1 - p.sigma) * this.localScales(planet, r, d).rad);
    const minimum = need / ((1 - p.sigma) * this.localScales(planet, r, d + first).rad);
    return (memo.bubble = Math.max(moon.bubbleRadius * p.bubbleScale, minimum, 2 * moon.radius));
  }

  /**
   * Per-moon memo of the bubble radius and altitude scale: within a frame every trail sample near a moon asks with
   * the same planet distance, moon distance and profile (S3-07 budget: ≤ 0.2 ms extra per frame).
   */
  private memoFor(moon: MappedBody, r: number, d: number): { bubble: number; h: number } {
    let m = this.moonMemo.get(moon.id);
    if (!m) this.moonMemo.set(moon.id, (m = { key: new Float64Array(12), bubble: Number.NaN, h: Number.NaN }));
    const p = this.profile;
    const k = m.key;
    const same =
      k[0] === r &&
      k[1] === d &&
      k[2] === p.alpha &&
      k[3] === p.bodyEnlarge &&
      k[4] === p.moonEnlarge &&
      k[5] === p.enlargedRefDu &&
      k[6] === p.enlargedExponent &&
      k[7] === p.localLinear &&
      k[8] === p.bubbleMatch &&
      k[9] === p.hScale &&
      k[10] === p.bubbleScale &&
      k[11] === p.sigma;
    if (!same) {
      k.set([r, d, p.alpha, p.bodyEnlarge, p.moonEnlarge, p.enlargedRefDu, p.enlargedExponent, p.localLinear]);
      k[8] = p.bubbleMatch;
      k[9] = p.hScale;
      k[10] = p.bubbleScale;
      k[11] = p.sigma;
      m.bubble = Number.NaN;
      m.h = Number.NaN;
    }
    return m;
  }

  private readonly moonMemo = new Map<string, { key: Float64Array; bubble: number; h: number }>();

  /** Logarithmic altitude scale H of a moon (du), matching the planet's local mapping at the moon bubble's edge. */
  moonAltitudeScale(moon: MappedBody, planet: MappedBody, r: number, d: number): number {
    const rBub = this.moonBubbleRadius(moon, planet, r, d);
    const memo = this.memoFor(moon, r, d);
    if (!Number.isNaN(memo.h)) return memo.h;
    const p = this.profile;
    const { rad, tan } = this.localScales(planet, r, d);
    const match = rad + (tan - rad) * p.bubbleMatch;
    const rPrime = this.displayRadius(moon);
    const h = (rBub * match - rPrime) / Math.log(1 + (rBub - moon.radius) / moon.radius);
    return (memo.h = Math.max(h, 0.01 * rPrime) * p.hScale);
  }

  /** A moon's altitude mapping h(a) in du for an altitude a in km (planet at r, moon at d from it). */
  moonAltitude(moon: MappedBody, planet: MappedBody, r: number, d: number, a: number): number {
    const p = this.profile;
    const lin = a / KM_PER_DU;
    if (p.localLinear >= 1) return lin;
    const log = this.moonAltitudeScale(moon, planet, r, d) * Math.log1p(a / moon.radius);
    return (1 - p.localLinear) * log + p.localLinear * lin;
  }

  /** Global mapping M_g(p) → du. */
  mapGlobal(p: Vec3, out: Vec3): Vec3 {
    const r = Math.hypot(p.x, p.y, p.z);
    const f = r <= 0 ? 0 : this.g(r) / r;
    out.x = p.x * f;
    out.y = p.y * f;
    out.z = p.z * f;
    return out;
  }

  /**
   * Maps a point given relative to a body: `b` = true body position (km), `q` = offset from the body (km).
   * Outside the body's bubble this equals the global mapping of b + q.
   */
  mapRelative(body: MappedBody, b: Vec3, q: Vec3, out: Vec3): Vec3 {
    const rb = Math.hypot(b.x, b.y, b.z);
    const rBub = this.bubbleRadius(body, rb);
    const d = Math.hypot(q.x, q.y, q.z);
    const sigma = this.profile.sigma;
    const w = smoothstep((d - (1 - sigma) * rBub) / (sigma * rBub));

    const pg = this.tmpG;
    if (w > 0) {
      pg.x = b.x + q.x;
      pg.y = b.y + q.y;
      pg.z = b.z + q.z;
      this.mapGlobal(pg, pg);
      if (w >= 1) {
        out.x = pg.x;
        out.y = pg.y;
        out.z = pg.z;
        return out;
      }
    }

    const bd = this.mapGlobal(b, this.tmpB);
    const a = Math.max(d - body.radius, 0);
    const len = this.displayRadius(body) + this.altitude(body, rb, a);
    let ux = 1,
      uy = 0,
      uz = 0;
    if (d > 0) {
      ux = q.x / d;
      uy = q.y / d;
      uz = q.z / d;
    }
    const lx = bd.x + len * ux;
    const ly = bd.y + len * uy;
    const lz = bd.z + len * uz;
    out.x = lx + (pg.x - lx) * w;
    out.y = ly + (pg.y - ly) * w;
    out.z = lz + (pg.z - lz) * w;
    return out;
  }

  /**
   * Maps a point near a moon with a nested bubble (D8): `b` = planet position (km, heliocentric), `qm` = moon relative
   * to the planet, `q` = point relative to the moon. Outside the moon's bubble this equals the planet's mapping of
   * b + qm + q.
   */
  mapMoonRelative(moon: MappedBody, planet: MappedBody, b: Vec3, qm: Vec3, q: Vec3, out: Vec3): Vec3 {
    const r = Math.hypot(b.x, b.y, b.z);
    const dm = Math.hypot(qm.x, qm.y, qm.z);
    const rBub = this.moonBubbleRadius(moon, planet, r, dm);
    const d = Math.hypot(q.x, q.y, q.z);
    const sigma = this.profile.sigma;
    const w = smoothstep((d - (1 - sigma) * rBub) / (sigma * rBub));

    // the planet's local mapping of the point (the outer side of the moon's shell)
    const pq = this.tmpQ2;
    pq.x = qm.x + q.x;
    pq.y = qm.y + q.y;
    pq.z = qm.z + q.z;
    const pl = this.tmpM;
    if (w > 0) {
      this.mapRelative(planet, b, pq, pl);
      if (w >= 1) {
        out.x = pl.x;
        out.y = pl.y;
        out.z = pl.z;
        return out;
      }
    }
    const lx0 = pl.x,
      ly0 = pl.y,
      lz0 = pl.z;
    const md = this.mapRelative(planet, b, qm, out); // the moon's display position (written into out)
    const a = Math.max(d - moon.radius, 0);
    const len = this.displayRadius(moon) + this.moonAltitude(moon, planet, r, dm, a);
    let ux = 1,
      uy = 0,
      uz = 0;
    if (d > 0) {
      ux = q.x / d;
      uy = q.y / d;
      uz = q.z / d;
    }
    const lx = md.x + len * ux;
    const ly = md.y + len * uy;
    const lz = md.z + len * uz;
    out.x = lx + (lx0 - lx) * w;
    out.y = ly + (ly0 - ly) * w;
    out.z = lz + (lz0 - lz) * w;
    return out;
  }

  /** The planet-level body whose (effective) bubble contains `p`. Bodies without a position (hidden) are skipped. */
  bubbleOf(p: Vec3, bodyPos: BodyPositionLookup): MappedBody | undefined {
    for (const body of this.bubbleBodies) {
      const b = bodyPos(body.id);
      if (!b) continue;
      const dx = p.x - b.x,
        dy = p.y - b.y,
        dz = p.z - b.z;
      const rBub = this.bubbleRadius(body, Math.hypot(b.x, b.y, b.z));
      if (dx * dx + dy * dy + dz * dz < rBub * rBub) return body;
    }
    return undefined;
  }

  /**
   * The moon of `planet` whose nested bubble contains the point `q` (relative to the planet), if any. `bodyPos` gives
   * heliocentric positions; the planet is at `b`.
   */
  moonBubbleOf(planet: MappedBody, b: Vec3, q: Vec3, bodyPos: BodyPositionLookup): MappedBody | undefined {
    const moons = this.moonsByParent.get(planet.id);
    if (!moons) return undefined;
    const r = Math.hypot(b.x, b.y, b.z);
    for (const moon of moons) {
      const mp = bodyPos(moon.id);
      if (!mp) continue;
      const qmx = mp.x - b.x,
        qmy = mp.y - b.y,
        qmz = mp.z - b.z;
      const dx = q.x - qmx,
        dy = q.y - qmy,
        dz = q.z - qmz;
      const rBub = this.moonBubbleRadius(moon, planet, r, Math.hypot(qmx, qmy, qmz));
      if (dx * dx + dy * dy + dz * dz < rBub * rBub) return moon;
    }
    return undefined;
  }

  /** The bubbles containing `p`: planet and nested moon (the returned object is reused). */
  locate(p: Vec3, bodyPos: BodyPositionLookup): BubbleChain {
    const c = this.chain;
    c.planet = this.bubbleOf(p, bodyPos);
    c.moon = undefined;
    if (c.planet) {
      const b = bodyPos(c.planet.id)!;
      const q = this.tmpQ;
      q.x = p.x - b.x;
      q.y = p.y - b.y;
      q.z = p.z - b.z;
      c.moon = this.moonBubbleOf(c.planet, b, q, bodyPos);
    }
    return c;
  }

  /**
   * Maps a point given relative to a planet (`q`, km), through a nested moon bubble if it lies in one. `bodyPos`
   * gives the moons' heliocentric positions.
   */
  mapInPlanet(planet: MappedBody, b: Vec3, q: Vec3, bodyPos: BodyPositionLookup, out: Vec3): Vec3 {
    const moon = this.moonBubbleOf(planet, b, q, bodyPos);
    if (!moon) return this.mapRelative(planet, b, q, out);
    const mp = bodyPos(moon.id)!;
    const qm = this.tmpQm;
    qm.x = mp.x - b.x;
    qm.y = mp.y - b.y;
    qm.z = mp.z - b.z;
    const qq = this.tmpB2;
    qq.x = q.x - qm.x;
    qq.y = q.y - qm.y;
    qq.z = q.z - qm.z;
    return this.mapMoonRelative(moon, planet, b, qm, qq, out);
  }

  private readonly tmpB2 = vec3();

  /** Full mapping M(p) of a heliocentric point; `bodyPos` gives true body positions at the same time. */
  mapPoint(p: Vec3, bodyPos: BodyPositionLookup, out: Vec3): Vec3 {
    const planet = this.bubbleOf(p, bodyPos);
    if (!planet) return this.mapGlobal(p, out);
    const b = bodyPos(planet.id)!;
    const q = this.tmpQ;
    q.x = p.x - b.x;
    q.y = p.y - b.y;
    q.z = p.z - b.z;
    return this.mapInPlanet(planet, b, q, bodyPos, out);
  }

  private readonly tmpQ = vec3();
}
